import React, { useEffect, useMemo, useState } from "react";
import SearchableSelect from "../../../components/common/SearchableSelect";
import {
  fetchAdminJson,
  fetchAllPages,
  downloadSampleTemplate,
  roleLabel,
  roleBadgeStyle,
  CredentialsModal,
  AdminDisclosureCard,
} from "../settingsHelpers";

export function UsersTab({ onContextChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ full_name: "", phone_number: "", email: "", role: "agent", username: "", password: "", reports_to: "", dealer_oem: "", dealer_name: "", dealer_city: "" });
  const [invRows, setInvRows] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [provisioningId, setProvisioningId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function loadUsers() {
    try {
      setLoading(true);
      const data = await fetchAllPages("/api/accounts/users/");
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  // Load inventory once when the dealer role is first selected (for OEM/Dealer/City pickers).
  useEffect(() => {
    if (form.role === "dealer" && invRows.length === 0) {
      fetchAdminJson("/api/crm/inventory/")
        .then((data) => setInvRows(data?.rows || []))
        .catch(() => {});
    }
  }, [form.role, invRows.length]);

  // Cascading dealer-identity options derived from inventory rows.
  const dealerOemOptions = useMemo(() => (
    [...new Set(invRows
      .filter((r) => (!form.dealer_city || r.city === form.dealer_city) && (!form.dealer_name || r.dealer === form.dealer_name))
      .map((r) => r.oem).filter(Boolean))].sort()
  ), [invRows, form.dealer_city, form.dealer_name]);

  const dealerNameOptions = useMemo(() => (
    [...new Set(invRows
      .filter((r) => (!form.dealer_city || r.city === form.dealer_city) && (!form.dealer_oem || r.oem === form.dealer_oem))
      .map((r) => r.dealer).filter(Boolean))].sort()
  ), [invRows, form.dealer_city, form.dealer_oem]);

  const dealerCityOptions = useMemo(() => (
    [...new Set(invRows
      .filter((r) => (!form.dealer_oem || r.oem === form.dealer_oem) && (!form.dealer_name || r.dealer === form.dealer_name))
      .map((r) => r.city).filter(Boolean))].sort()
  ), [invRows, form.dealer_oem, form.dealer_name]);

  async function handleCreateUser(e) {
    if (e) e.preventDefault();
    try {
      setError("");
      setNotice("");
      const submitForm = { ...form };
      if (!submitForm.username) delete submitForm.username;
      if (!submitForm.password) delete submitForm.password;
      if (!submitForm.reports_to) delete submitForm.reports_to;
      else submitForm.reports_to = Number(submitForm.reports_to);
      if (submitForm.role === "dealer") {
        if (!submitForm.dealer_oem || !submitForm.dealer_city) {
          setError("OEM and Dealer City are required for a dealer user.");
          return;
        }
      } else {
        delete submitForm.dealer_oem;
        delete submitForm.dealer_name;
        delete submitForm.dealer_city;
      }
      const payload = await fetchAdminJson("/api/accounts/users/", {
        method: "POST",
        body: JSON.stringify(submitForm),
      });
      setNotice("User created successfully.");
      setCredentialsModal([{
        full_name: payload.user?.full_name || submitForm.full_name,
        username: payload.user?.username || "",
        email: payload.user?.email || submitForm.email,
        role: payload.user?.role || submitForm.role,
        password: payload.generated_password || submitForm.password || "",
      }]);
      setForm({ full_name: "", phone_number: "", email: "", role: "agent", username: "", password: "", reports_to: "", dealer_oem: "", dealer_name: "", dealer_city: "" });
      await loadUsers();
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    }
  }

  function openEdit(user) {
    setEditingUser(user.id);
    setEditForm({ full_name: user.full_name, phone_number: user.phone_number || "", role: user.role, is_active: user.is_active, ozonetel_agent_id: user.ozonetel_agent_id || "", reports_to: user.reports_to ? String(user.reports_to) : "" });
  }

  async function handleEditSave(userId) {
    try {
      setError("");
      const payload = { ...editForm };
      if (payload.reports_to) payload.reports_to = Number(payload.reports_to);
      else payload.reports_to = null;
      await fetchAdminJson(`/api/accounts/users/${userId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setNotice("User updated.");
      setEditingUser(null);
      await loadUsers();
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeactivate(userId, userName) {
    if (!window.confirm(`Deactivate ${userName}? They will lose access to the CRM.`)) return;
    try {
      setError("");
      await fetchAdminJson(`/api/accounts/users/${userId}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: false }),
      });
      setNotice(`${userName} has been deactivated.`);
      await loadUsers();
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleActivate(userId, userName) {
    try {
      setError("");
      await fetchAdminJson(`/api/accounts/users/${userId}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: true }),
      });
      setNotice(`${userName} has been activated.`);
      await loadUsers();
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleResetPassword(user) {
    if (!window.confirm(`Reset password for ${user.full_name || user.username}? A new password will be generated.`)) return;
    try {
      setError("");
      const data = await fetchAdminJson(`/api/accounts/users/${user.id}/reset-password/`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      window.prompt(
        `New password for ${data.username}. Copy and share with the user — it won't be shown again.`,
        data.generated_password,
      );
      setNotice(`Password reset for ${user.full_name || user.username}.`);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleBulkUpload(e) {
    if (e) e.preventDefault();
    if (!bulkFile) return;
    try {
      setError("");
      setBulkResults(null);
      setNotice("Uploading...");
      const formData = new FormData();
      formData.append("file", bulkFile);
      const data = await fetchAdminJson("/api/accounts/users/bulk-create/", {
        method: "POST",
        body: formData,
      });
      setBulkResults(data);
      setNotice(`${data.count || 0} user(s) created.`);
      if (data.created?.length > 0) {
        setCredentialsModal(data.created.map((row) => ({
          full_name: row.user?.full_name || "",
          username: row.user?.username || "",
          email: row.user?.email || "",
          role: row.user?.role || "",
          password: row.generated_password || "",
        })));
      }
      setBulkFile(null);
      await loadUsers();
      onContextChange?.();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
    <div className="settings-stack">
      {error && <div className="message-banner error">{error}</div>}
      {notice && <div className="message-banner success">{notice}</div>}
      <div className="settings-form-grid">
        <AdminDisclosureCard
          title="Add Single User"
          description="Create one CRM user with role, email, and phone details."
          actionLabel="Open Form"
          open={createOpen}
          onToggle={() => setCreateOpen((current) => !current)}
        >
          <form className="form-grid settings-form-card-inner" onSubmit={handleCreateUser}>
            <label className="field-stack">
              <span>Full Name <span className="field-required">*</span></span>
              <input required value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} />
            </label>
            <label className="field-stack">
              <span>Email <span className="field-required">*</span></span>
              <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            </label>
            <label className="field-stack">
              <span>Phone <span className="field-required">*</span></span>
              <input required value={form.phone_number} onChange={(e) => setForm({...form, phone_number: e.target.value})} />
            </label>
            <label className="field-stack">
              <span>Role</span>
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value, reports_to: ""})}>
                <option value="admin">Admin</option>
                <option value="senior_manager">Senior Manager</option>
                <option value="manager">Manager</option>
                <option value="agent">Agent</option>
                <option value="btl">BTL Worker</option>
                <option value="dealer">Dealer</option>
              </select>
            </label>
            {form.role === "dealer" && (
              <>
                <label className="field-stack">
                  <span>OEM <span className="field-required">*</span></span>
                  <SearchableSelect
                    value={form.dealer_oem}
                    onChange={(v) => setForm({ ...form, dealer_oem: v })}
                    options={dealerOemOptions}
                    placeholder="Select OEM…"
                  />
                </label>
                <label className="field-stack">
                  <span>Dealer Name <span className="field-optional">Optional</span></span>
                  <SearchableSelect
                    value={form.dealer_name}
                    onChange={(v) => setForm({ ...form, dealer_name: v })}
                    options={dealerNameOptions}
                    placeholder="Select dealer…"
                  />
                </label>
                <label className="field-stack">
                  <span>Dealer City <span className="field-required">*</span></span>
                  <SearchableSelect
                    value={form.dealer_city}
                    onChange={(v) => setForm({ ...form, dealer_city: v })}
                    options={dealerCityOptions}
                    placeholder="Select city…"
                  />
                </label>
              </>
            )}
            {(form.role === "agent" || form.role === "btl") && (
              <label className="field-stack">
                <span>Reporting To <span className="field-optional">Optional</span></span>
                <select value={form.reports_to} onChange={(e) => setForm({...form, reports_to: e.target.value})}>
                  <option value="">— No manager —</option>
                  {users.filter((u) => u.role === "manager" && u.is_active).map((mgr) => (
                    <option key={mgr.id} value={mgr.id}>{mgr.full_name}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="field-stack">
              <span>Username <span className="field-optional">Optional</span></span>
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="Auto-generated from email if blank"
              />
            </label>
            <label className="field-stack">
              <span>Password <span className="field-optional">Optional</span></span>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Auto-generated if blank"
              />
            </label>
            <div className="full-span settings-form-card-actions">
              <button className="primary-button" type="submit">Create User</button>
            </div>
          </form>
        </AdminDisclosureCard>

        <AdminDisclosureCard
          title="Bulk Create Users"
          description="Upload a CSV or XLSX file to create multiple users in one batch."
          actionLabel="Open Upload"
          open={bulkOpen}
          onToggle={() => setBulkOpen((current) => !current)}
        >
          <div className="settings-form-card-inner" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Step 1 — Download template */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: "#eef2ff", color: "#4f6bed", fontWeight: 700, fontSize: 13,
              }}>1</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "var(--text-primary)" }}>Download a sample template</p>
                <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
                  Use one of the templates below. Required columns: <strong>full_name</strong>, <strong>email</strong>, <strong>phone_number</strong>, <strong>role</strong>. Optional: username, password (auto-generated if blank). For <strong>dealer</strong> rows, also fill <strong>dealer_oem</strong> and <strong>dealer_city</strong> (dealer_name optional).
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    onClick={() => downloadSampleTemplate("xlsx").catch(() => setError("Failed to download template."))}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                    Sample XLSX
                  </button>
                  <button
                    type="button"
                    className="secondary-ghost compact-button"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    onClick={() => downloadSampleTemplate("csv").catch(() => setError("Failed to download template."))}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                    Sample CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Step 2 — Upload file */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: "#eef2ff", color: "#4f6bed", fontWeight: 700, fontSize: 13,
              }}>2</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "var(--text-primary)" }}>Upload your file</p>
                <form onSubmit={handleBulkUpload}>
                  <label
                    className="file-upload-field settings-file-upload"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                      padding: "20px 16px", cursor: "pointer", textAlign: "center",
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke={bulkFile ? "#22c55e" : "#9aa5ba"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 10l-4-4-4 4M10 6v8M4 16h12" />
                    </svg>
                    <span style={{ fontSize: 13, color: bulkFile ? "var(--text-primary)" : "var(--text-secondary)" }}>
                      {bulkFile ? bulkFile.name : "Click to choose CSV or XLSX file"}
                    </span>
                    <input type="file" accept=".csv,.xlsx" onChange={(e) => setBulkFile(e.target.files[0])} style={{ display: "none" }} />
                  </label>
                  <button
                    className="primary-button"
                    type="submit"
                    disabled={!bulkFile}
                    style={{ marginTop: 12, width: "100%" }}
                  >
                    Upload and Create Users
                  </button>
                </form>
              </div>
            </div>

            {bulkResults?.errors?.length > 0 && (
              <div className="message-banner error">
                {bulkResults.errors.map((e, i) => (
                  <div key={i}>{e.entry}: {e.error}</div>
                ))}
              </div>
            )}
          </div>
        </AdminDisclosureCard>
      </div>

      <div className="settings-card settings-table-card">
        <div className="settings-card-header">
          <div>
            <p className="login-kicker">Users</p>
            <h3>Manage CRM Users</h3>
          </div>
          <span className="agent-badge mint">{users.length} total</span>
        </div>
        <div className="table-scroll report-table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Ozonetel ID</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7">Loading...</td></tr>
            ) : users.map(u => (
              <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                <td>
                  {editingUser === u.id ? (
                    <input
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                      style={{ width: "100%", fontSize: "13px" }}
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontWeight: 500 }}>{u.full_name || "—"}</span>
                      <span style={{ fontSize: "11px", color: "var(--text-secondary)" }}>@{u.username}</span>
                    </div>
                  )}
                </td>
                <td>{u.email}</td>
                <td>
                  {editingUser === u.id ? (
                    <input
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      style={{ width: "100%", fontSize: "13px" }}
                    />
                  ) : u.phone_number || "—"}
                </td>
                <td>
                  {editingUser === u.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value, reports_to: "" })}
                        style={{ fontSize: "13px" }}
                      >
                        <option value="admin">Admin</option>
                        <option value="senior_manager">Senior Manager</option>
                        <option value="manager">Manager</option>
                        <option value="agent">Agent</option>
                        <option value="btl">BTL Worker</option>
                      </select>
                      {(editForm.role === "agent" || editForm.role === "btl") && (
                        <select
                          value={editForm.reports_to || ""}
                          onChange={(e) => setEditForm({ ...editForm, reports_to: e.target.value })}
                          style={{ fontSize: "12px" }}
                          title="Reporting To"
                        >
                          <option value="">— Reporting To —</option>
                          {users.filter((mgr) => mgr.role === "manager" && mgr.is_active && mgr.id !== u.id).map((mgr) => (
                            <option key={mgr.id} value={mgr.id}>{mgr.full_name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  ) : (
                    <div>
                      <span style={roleBadgeStyle(u.role)}>{roleLabel(u.role)}</span>
                      {(u.role === "agent" || u.role === "btl") && u.reports_to_name && (
                        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>→ {u.reports_to_name}</div>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  {editingUser === u.id ? (
                    <input
                      value={editForm.ozonetel_agent_id}
                      onChange={(e) => setEditForm({ ...editForm, ozonetel_agent_id: e.target.value })}
                      placeholder="e.g. crm_agent_b"
                      style={{ width: "100%", fontSize: "13px" }}
                    />
                  ) : (
                    <span style={{ fontFamily: "monospace", fontSize: "12px", color: u.ozonetel_agent_id ? "var(--text-primary)" : "var(--text-secondary)" }}>
                      {u.ozonetel_agent_id || "—"}
                    </span>
                  )}
                </td>
                <td>
                  <span style={{ color: u.is_active ? "var(--green)" : "var(--text-secondary)", fontWeight: 600, fontSize: "12px" }}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div className="settings-user-actions">
                    {editingUser === u.id ? (
                      <>
                        <button className="primary-button compact-button settings-action-button" onClick={() => handleEditSave(u.id)}>Save</button>
                        <button className="secondary-ghost compact-button settings-action-button" onClick={() => setEditingUser(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="secondary-ghost compact-button settings-action-button" onClick={() => openEdit(u)}>Edit</button>
                        <button
                          className="secondary-ghost compact-button settings-action-button"
                          onClick={() => handleResetPassword(u)}
                          title="Generate a new password for this user"
                        >Reset Password</button>
                        {u.is_active ? (
                          <button
                            className="compact-button settings-action-button settings-action-button-danger"
                            onClick={() => handleDeactivate(u.id, u.full_name || u.username)}
                          >Deactivate</button>
                        ) : (
                          <button
                            className="compact-button settings-action-button"
                            style={{ borderColor: "#b8e6c8", color: "#22874a" }}
                            onClick={() => handleActivate(u.id, u.full_name || u.username)}
                          >Activate</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>

    {credentialsModal && (
      <CredentialsModal rows={credentialsModal} onClose={() => setCredentialsModal(null)} />
    )}
    </>
  );
}

export default UsersTab;
